import {
  BoostersReward,
  FeatureReward,
  Match2Reward,
  Match2RewardType,
  MultiplierReward,
  PowerupsReward,
  PredefinedRewardId,
  RewardSet,
  RewardWrapper,
  isRandomReward,
  isTimedReward,
} from 'src/replicant/match2/rewards/match2-rewards.ruleset';
import { getPowerBlockMap } from '../game/defs/powerBlocks';
import { assertNever } from 'src/replicant/utils';
import {
  getActiveRace,
  getRaceStartTime,
} from 'src/replicant/match2/races/races.getters';
import { getRaceTheme } from '../races/races.utils';
import { user, now } from 'src/StateObserver';
import { getMatch2FrenzyEventConfig } from 'src/replicant/match2/frenzy/match2-frenzy.getters';
import i18n from 'src/lib/i18n/i18n';
import { Match2PowerupType } from 'src/replicant/match2/match2.ruleset';
import { BoosterId } from '../defs/booster';
import { View } from '@play-co/timestep-core/ui';
import { ViewPosition } from '@play-co/timestep-core/lib/ui/View';
import {
  animateCoins,
  animateEnergy,
  animateGems,
  animateMatch2Boosters,
  animateMatch2FrenzyObjective,
  animateMatch2Moves,
  animateMatch2Powerups,
  animateMatch2RacePoints,
  animateTeamEventPoints,
} from 'src/game/animation/CurrencyAnimation';
import { processMatch2Rewards } from 'src/replicant/match2/rewards/match2-rewards.getters';
import { getPartOfTime } from 'src/replicant/utils/duration';
import { formatDuration } from 'src/lib/formatDuration';
import {
  openPopupPromise,
  PopupDialogOrigin,
} from 'src/lib/popups/popupOpenClose';
import { getFrenzyObjectiveIcon, getRewardIcon } from '../match2.icons';
import { getMatch2AssetPath } from '../game/util/assets';
import { matchTheme } from 'src/game/theme/match2/Match2Theme';
import {
  cardsAnimateRewardIconToHomeButton,
  cardsAnimatePackOpenFromIcon,
} from 'src/game/cards/cards.utils';
import {
  cardsGetCardIcon,
  cardsGetPackIcon,
} from 'src/game/cards/cards.assets';

export type Match2RewardsPopupType = 'default' | 'chest';

interface Match2RewardsPopupOpts {
  rewards: Match2Reward[];
  useSfx?: boolean;
  noGrantAnimation?: boolean;
  stars?: number;
  tracking?: { feature: string; $subFeature: string };
  origin: PopupDialogOrigin | undefined;
}

export async function openMatch2RewardsPopup({
  rewards,
  noGrantAnimation = false,
  useSfx = false,
  stars,
  tracking,
  origin,
}: Match2RewardsPopupOpts) {
  return openPopupPromise('popupMatch2GrantRewards', {
    rewards,
    sound: useSfx ? 'applause' : undefined,
    useClickOutside: true,
    tracking,
    noGrantAnimation,
    stars,
    origin,
  });
}

// Store callback outside Redux to avoid serialization warning
let pendingOnBeforeClaim: (() => Promise<boolean>) | undefined;

export function getMatch2ChestOnBeforeClaim():
  | (() => Promise<boolean>)
  | undefined {
  return pendingOnBeforeClaim;
}

export async function openMatch2ChestPopup({
  rewards,
  chestType = 'green',
  origin,
  onBeforeClaim,
}: {
  rewards: Match2Reward[];
  chestType?: ChestType;
  origin: PopupDialogOrigin | undefined;
  onBeforeClaim?: () => Promise<boolean>;
}): Promise<boolean> {
  // Store callback in module scope to avoid passing through Redux
  pendingOnBeforeClaim = onBeforeClaim;
  try {
    return await openPopupPromise('popupMatch2Chest', {
      rewards,
      chestType,
      origin,
    });
  } finally {
    pendingOnBeforeClaim = undefined;
  }
}

const rewardAssetMap: Partial<Record<Match2RewardType, string>> = {
  bPoints: 'assets/ui/shared/icons/coin.png',
  gems: 'assets/ui/shared/icons/gem.png',
  energy: 'assets/ui/match2/common/pinch_energy.png',
  moves: 'assets/ui/match2/common/pinch_moves.png',
};

export async function getMatch2RewardAsset(reward: Match2Reward) {
  const { type } = reward;
  switch (type) {
    case 'bPoints':
    case 'gems':
    case 'energy':
    case 'moves':
      return rewardAssetMap[type];
    case 'boosters':
      return getBoosterIcon(reward);
    case 'powerups':
      return getPowerupIcon(reward);
    case 'multiplier':
      return getMultiplierRewardIcon(reward);
    case 'feature':
      return getFeatureRewardIcon(reward);
    case 'reward_set':
    case 'wrapper':
      return getDecorationIcon(reward);
    case 'roomDecoration':
      return getRoomDecorationIcon();
    case 'cardPack':
      return cardsGetPackIcon(reward.tier);
    case 'card':
      return cardsGetCardIcon(reward.cardId);
    default:
      assertNever(type);
  }
}

function getBoosterIcon(reward: BoostersReward) {
  if (isRandomReward(reward)) return getMatch2BundleIcon('all_powerups');
  return matchTheme().getBoosterIcon(reward.subType);
}

function getPowerupIcon(reward: PowerupsReward) {
  if (isRandomReward(reward)) return getMatch2BundleIcon('all_powerups');
  return `${getMatch2AssetPath(getPowerBlockMap()[reward.subType])}`;
}

function getMultiplierRewardIcon(reward: MultiplierReward) {
  const { subType } = reward;
  switch (subType) {
    case 'frenzy':
      return getMatch2FrenzyObjectiveIcon();
    default:
      assertNever(subType);
  }
}

function getFeatureRewardIcon(reward: FeatureReward) {
  const { subType } = reward;
  switch (subType) {
    case 'frenzy_objective':
      return getMatch2FrenzyObjectiveIcon();
    case 'race_points': {
      const { config } = getActiveRace(getRaceStartTime(reward.raceId))!;
      // Should never happen, but use a placeholder just in case.
      if (config.type === 'target')
        return 'assets/ui/match2/common/icon_star.png';
      return getRaceTheme(config).icon.image!;
    }
    case 'team_event':
    case 'team_competition_event':
      return 'assets/ui/match2/team/competition/icon_teamCompetition_sm.png';
    case 'team_chest_event':
      return 'assets/ui/match2/team/chest/team_chest_icon.png';
    case 'superLollipop':
      return 'assets/ui/superLollipop/shared/lollipop_icon.png';
    default:
      assertNever(subType);
  }
}

function getMatch2FrenzyObjectiveIcon() {
  const config = getMatch2FrenzyEventConfig(user(), now());
  if (!config) throw new Error(`No frenzy event config found`);
  return getFrenzyObjectiveIcon(config.theme).get();
}

function getDecorationIcon(reward: RewardSet | RewardWrapper) {
  if (!reward.decoration) return getMatch2BundleIcon('all_powerups');
  const { decoration } = reward;
  const { type } = decoration;
  switch (type) {
    case 'bundle':
      if (decoration.id) return getMatch2BundleIcon(decoration.id);
      else if ('id' in reward) return getMatch2BundleIcon(reward.id);
      else return getMatch2BundleIcon('all_powerups');
    case 'chest':
      return getMatch2ChestIcon(decoration.subType);
    default:
      assertNever(type);
  }
}

async function getRoomDecorationIcon() {
  return `assets/ui/decoration/icons/living_icon.png`;
}

export function getMatch2BundleIcon(id: PredefinedRewardId) {
  const map: Partial<Record<PredefinedRewardId, Match2Reward[]>> = {
    all_boosters: [
      {
        type: 'boosters',
        subType: 'roulette',
        amount: 1,
      },
      {
        type: 'boosters',
        subType: 'drill',
        amount: 1,
      },
      {
        type: 'boosters',
        subType: 'bullet',
        amount: 1,
      },
    ],
    all_powerups: [
      {
        type: 'powerups',
        subType: 'rocket',
        amount: 1,
      },
      {
        type: 'powerups',
        subType: 'bomb',
        amount: 1,
      },
      {
        type: 'powerups',
        subType: 'cube',
        amount: 1,
      },
    ],
    firecracker_balloon: [
      {
        type: 'powerups',
        subType: 'rocket',
        amount: 1,
      },
      {
        type: 'powerups',
        subType: 'bomb',
        amount: 1,
      },
    ],
    lollipop_firecracker: [
      {
        type: 'powerups',
        subType: 'rocket',
        amount: 1,
      },
      {
        type: 'powerups',
        subType: 'cube',
        amount: 1,
      },
    ],
  };
  return getRewardIcon(map[id] ?? map.all_powerups!).get();
}

export type ChestType = 'green' | 'pink' | 'team';

export function getMatch2ChestIcon(type: ChestType) {
  return `assets/ui/match2/chest/icon_${type}.png`;
}

const rewardNameMap: Partial<Record<Match2RewardType, string>> = {
  bPoints: 'shopMenu.coins',
  gems: 'shopMenu.gems',
  energy: 'shopMenu.energy',
  moves: 'match2.rewards.moves',
};

export function getRewardName(reward: Match2Reward) {
  if (isRandomReward(reward)) return '???';
  const { type } = reward;
  switch (type) {
    case 'bPoints':
    case 'gems':
    case 'energy':
    case 'moves':
      return i18n(rewardNameMap[type]!);
    case 'powerups':
      return getPowerupName(reward.subType);
    case 'boosters':
      return getBoosterName(reward.subType);
    case 'roomDecoration':
      return i18n(`match2.rewards.decorations`);
    case 'wrapper':
      if ('id' in reward) {
        if (reward.id === 'all_boosters' || reward.id === 'all_powerups') {
          return i18n(`match2.rewards.boosters`);
        }
      }
      return i18n(`match2.rewards.generic`);
    case 'multiplier':
    case 'feature':
    case 'reward_set':
    case 'cardPack':
    case 'card':
      return i18n(`match2.rewards.generic`);
    default:
      assertNever(type);
  }
}

function getPowerupName(type: Match2PowerupType) {
  return i18n(`match2.powerups.purchase.${type}.title`);
}

function getBoosterName(type: BoosterId) {
  return i18n(`match2.boosters.purchase.${type}.title`);
}

export function getMatch2RewardValue(reward: Match2Reward) {
  if (isTimedReward(reward)) {
    return getTimedText(reward.duration);
  } else if (!isRandomReward(reward) && 'amount' in reward) {
    return `${reward.amount}`;
  }
  return '';
}

function getTimedText(value: number) {
  const days = getPartOfTime(value, 'days', true);
  if (days > 0) return `${days}${i18n('duration.d')}`;

  const hours = getPartOfTime(value, 'hours', true);
  if (hours > 0) return `${hours}${i18n('duration.h')}`;

  const minutes = getPartOfTime(value, 'minutes', true);
  if (minutes > 0) return `${minutes}${i18n('duration.m')}`;

  return formatDuration(value);
}

export function getMatch2RewardDecoration(reward: Match2Reward) {
  if (!('decoration' in reward)) return null;
  return reward.decoration;
}

export async function animateRewards(
  superview: View,
  reward: Match2Reward | Match2Reward[],
  position: ViewPosition,
) {
  const animations: Promise<unknown>[] = [];

  if (Array.isArray(reward)) {
    const rewards = processMatch2Rewards(reward, now());
    rewards.forEach((reward) => {
      animations.push(animateReward(superview, reward, position));
    });
    return Promise.all(animations);
  }

  return animateReward(superview, reward, position);
}

async function animateReward(
  superview: View,
  reward: Match2Reward,
  position: ViewPosition,
) {
  if (isRandomReward(reward)) return;
  const amount = 'amount' in reward ? reward.amount : 1;
  const type = reward.type;

  switch (type) {
    case 'bPoints':
      return animateCoins({
        superview,
        start: { position, scale: 1 },
        count: amount,
      });
    case 'gems':
      return animateGems({
        superview,
        start: { position, scale: 1 },
        count: amount,
      });
    case 'energy':
      return animateEnergy({
        superview,
        start: { position, scale: 1 },
        count: amount,
      });
    case 'boosters':
      // 'dart', 'bullet', 'drill', 'roulette'
      return animateMatch2Boosters({
        superview,
        type: reward.subType,
        count: amount,
        start: { position, scale: 1 },
      });
    case 'powerups':
      // 'rocket', 'bomb', 'cube'
      return animateMatch2Powerups({
        superview,
        type: reward.subType,
        count: amount,
        start: { position, scale: 1 },
      });
    case 'feature': {
      const subType = reward.subType;
      switch (subType) {
        case 'frenzy_objective':
          return animateMatch2FrenzyObjective({
            superview,
            count: amount,
            start: { position, scale: 1 },
          });
        case 'race_points':
          return animateMatch2RacePoints({
            superview,
            count: amount,
            start: { position, scale: 1 },
            raceId: reward.raceId,
          });
        case 'team_event':
        case 'team_competition_event':
        case 'team_chest_event':
          return animateTeamEventPoints({
            superview,
            count: amount,
            start: { position, scale: 1 },
            type: subType,
          });
        case 'superLollipop':
          return openPopupPromise('popupSuperLollipopActive', {
            mode: 'tapToContinue',
          });
        default:
          return assertNever(subType);
      }
    }
    case 'moves':
      return animateMatch2Moves({
        superview,
        start: { position, scale: 1 },
        count: amount,
      });

    case 'reward_set':
    case 'wrapper': {
      const decoration = getMatch2RewardDecoration(reward);
      if (decoration?.type === 'bundle') {
        const animations: Promise<unknown>[] = [];
        const rewards = processMatch2Rewards([reward], now());
        rewards.forEach((reward) => {
          animations.push(animateReward(superview, reward, position));
        });
        return Promise.all(animations);
      }
      return;
    }
    case 'cardPack':
      return cardsAnimatePackOpenFromIcon({
        superview,
        position,
        tier: reward.tier,
      });
    case 'card':
      return cardsAnimateRewardIconToHomeButton({
        superview,
        position,
      });
    case 'multiplier':
    case 'roomDecoration':
      return;
    default:
      assertNever(type);
  }
}
